import { FormEvent, MouseEvent, useCallback, useEffect, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import axios from 'axios'
import moment from 'moment'
import { FaSearch, FaSpinner } from 'react-icons/fa'

import {
  Highlight,
  Hit,
  Kind,
  KIND_INFO,
  KINDS,
  MIN_CHARS,
  pathFor,
  quickSearch,
  rememberRecent,
  resultsPathFor,
} from '../../components/AppShell/searchShared'
import './SearchResults.css'

/**
 * Every job, quote and enquiry matching a search - where Enter in the header
 * search lands. The search text is in the address (/search?q=…), so a search
 * can be bookmarked, shared or come back to.
 *
 * It asks the same quick search as the header dropdown, so it finds exactly
 * what the dropdown found, only all of it: the newest of each kind first, with
 * the total for each, and "Show more" fetching the next lot. It also finds
 * diary entries - the matching jobs' bookings, or a crew member's - which the
 * dropdown leaves out to stay quick.
 */

const FIRST_TAKE = 25
const MORE_TAKE = 50

const SEARCHED = KINDS

type Filter = Kind | 'All'

interface Group {
  hits: Hit[]
  more: boolean
  total: number
}

const groupsFrom = (hits: Hit[], more: Kind[], counts: Partial<Record<Kind, number>>) =>
  Object.fromEntries(
    SEARCHED.map(({ kind }) => {
      const mine = hits.filter((h) => h.kind === kind)
      return [kind, { hits: mine, more: more.includes(kind), total: counts[kind] ?? mine.length }]
    })
  ) as Record<Kind, Group>

/** When a diary entry is: "Tue 04/11/2026 08:00-17:30", or across days. */
const whenFor = (start: string, end?: string) => {
  const s = moment(start)
  const e = end ? moment(end) : null
  if (!e) return s.format('ddd DD/MM/YYYY HH:mm')
  return s.isSame(e, 'day')
    ? `${s.format('ddd DD/MM/YYYY HH:mm')}-${e.format('HH:mm')}`
    : `${s.format('DD/MM/YYYY HH:mm')} - ${e.format('DD/MM/YYYY HH:mm')}`
}

const SearchResults = () => {
  const navigate = useNavigate()
  const [params, setParams] = useSearchParams()
  const q = (params.get('q') ?? '').trim()
  const typeParam = params.get('type') as Kind | null
  const filter: Filter = typeParam && SEARCHED.some((k) => k.kind === typeParam) ? typeParam : 'All'

  const [text, setText] = useState(q)
  const [groups, setGroups] = useState<Record<Kind, Group> | null>(null)
  const [loading, setLoading] = useState(false)
  const [failed, setFailed] = useState(false)
  const [loadingMore, setLoadingMore] = useState<Kind | null>(null)

  // The box follows the address, so a search from the header updates it.
  useEffect(() => setText(q), [q])

  useEffect(() => {
    if (q.length < MIN_CHARS) {
      setGroups(null)
      setFailed(false)
      setLoading(false)
      return
    }

    const controller = new AbortController()
    setLoading(true)
    setFailed(false)
    quickSearch(q, controller.signal, { take: FIRST_TAKE, counts: true, diary: true })
      .then((found) => {
        setGroups(groupsFrom(found.hits, found.more, found.counts))
        setLoading(false)
      })
      .catch((error) => {
        if (axios.isCancel(error)) return
        setGroups(null)
        setFailed(true)
        setLoading(false)
      })
    return () => controller.abort()
  }, [q])

  const search = (e: FormEvent) => {
    e.preventDefault()
    const next = text.trim()
    if (next.length < MIN_CHARS) return
    navigate(resultsPathFor(next) + (filter !== 'All' ? `&type=${filter}` : ''))
  }

  const choose = (next: Filter) => {
    const updated = new URLSearchParams(params)
    if (next === 'All') updated.delete('type')
    else updated.set('type', next)
    setParams(updated, { replace: true })
  }

  const showMore = useCallback(
    async (kind: Kind) => {
      const group = groups?.[kind]
      if (!group) return
      setLoadingMore(kind)
      try {
        const found = await quickSearch(q, new AbortController().signal, {
          type: kind,
          take: MORE_TAKE,
          skip: group.hits.length,
        })
        setGroups((current) =>
          current && {
            ...current,
            [kind]: {
              ...current[kind],
              // A record added since the first page can shift one along; keep it once.
              hits: [...current[kind].hits, ...found.hits.filter((h) => !current[kind].hits.some((c) => c.key === h.key))],
              more: found.more.includes(kind),
            },
          }
        )
      } catch {
        // What's showing stays; Show more can be tried again.
      } finally {
        setLoadingMore(null)
      }
    },
    [groups, q]
  )

  const open = (e: MouseEvent, hit: Hit) => {
    rememberRecent(hit)
    // Ctrl/Cmd or shift click opens a new tab or window, as any link does.
    if (e.metaKey || e.ctrlKey || e.shiftKey) return
    e.preventDefault()
    navigate(pathFor(hit))
  }

  const total = groups ? SEARCHED.reduce((sum, k) => sum + groups[k.kind].total, 0) : 0
  const shown = SEARCHED.filter((k) => groups && (filter === 'All' || filter === k.kind) && groups[k.kind].total > 0)

  const row = (hit: Hit) => {
    const info = KIND_INFO[hit.kind]
    return (
      <a
        key={hit.key}
        href={pathFor(hit)}
        className='srp-row'
        onClick={(e) => open(e, hit)}
        // A middle click opens a new tab by itself; just remember it was opened.
        onAuxClick={(e) => e.button === 1 && rememberRecent(hit)}
      >
        <span className={`psr-row__icon psr-tone--${info.tone}`} aria-hidden='true'>
          {info.icon}
        </span>
        <span className='srp-row__body'>
          <span className='srp-row__top'>
            {hit.ref && (
              <span className='srp-row__ref'>
                <Highlight text={hit.ref} query={q} />
              </span>
            )}
            <span className='srp-row__title'>
              <Highlight text={hit.title} query={q} />
            </span>
          </span>
          {hit.detail && (
            <span className='srp-row__detail'>
              <Highlight text={hit.detail} query={q} />
            </span>
          )}
          {(hit.place || hit.contact) && (
            <span className='srp-row__meta'>
              <Highlight text={[hit.place, hit.contact].filter(Boolean).join(' · ')} query={q} />
            </span>
          )}
        </span>
        <span className='srp-row__side'>
          <span className='srp-row__kind'>{hit.kind}</span>
          {hit.kind === 'Diary' && hit.startDate ? (
            <span className='srp-row__date'>{whenFor(hit.startDate, hit.endDate)}</span>
          ) : (
            hit.createdDate && <span className='srp-row__date'>{moment(hit.createdDate).format('DD/MM/YYYY')}</span>
          )}
        </span>
      </a>
    )
  }

  return (
    <div className='srp'>
      <header className='srp-head'>
        <h1 className='srp-head__title'>Search results</h1>
        <form className='srp-box' onSubmit={search} role='search'>
          {loading ? (
            <FaSpinner className='srp-box__icon psr-spin' aria-hidden='true' />
          ) : (
            <FaSearch className='srp-box__icon' aria-hidden='true' />
          )}
          <input
            autoFocus={!q}
            className='srp-box__input'
            value={text}
            onChange={(e) => setText(e.target.value)}
            placeholder='Search jobs, quotes, enquiries, clients…'
            aria-label='Search'
          />
          <button type='submit' className='srp-box__go' disabled={text.trim().length < MIN_CHARS}>
            Search
          </button>
        </form>

        {groups && total > 0 && (
          <div className='srp-tabs' role='tablist' aria-label='Show'>
            {(['All', ...SEARCHED.map((k) => k.kind)] as Filter[]).map((f) => {
              const count = f === 'All' ? total : groups[f].total
              return (
                <button
                  key={f}
                  type='button'
                  role='tab'
                  aria-selected={filter === f}
                  className={`srp-tab${filter === f ? ' srp-tab--on' : ''}`}
                  onClick={() => choose(f)}
                  disabled={f !== 'All' && count === 0}
                >
                  {f === 'All' ? 'All' : KIND_INFO[f].plural}
                  <span className='srp-tab__count'>{count.toLocaleString('en-GB')}</span>
                </button>
              )
            })}
          </div>
        )}
      </header>

      <div className='srp-body'>
        {q.length < MIN_CHARS ? (
          <p className='srp-hint'>
            Type at least {MIN_CHARS} letters to search jobs, quotes and enquiries - by reference (J004498), client,
            contact, address, postcode, phone or email.
          </p>
        ) : failed ? (
          <p className='srp-hint'>Search isn't available right now. Try again in a moment.</p>
        ) : !groups ? (
          <p className='srp-hint'>Searching…</p>
        ) : total === 0 ? (
          <p className='srp-hint'>
            Nothing matches “<strong>{q}</strong>”. Check the spelling, or search for fewer words.
          </p>
        ) : (
          shown.map(({ kind, plural }) => {
            const group = groups[kind]
            return (
              <section key={kind} className='srp-group' aria-label={plural}>
                <div className='srp-group__head'>
                  <h2>
                    {plural} <span className='srp-group__count'>{group.total.toLocaleString('en-GB')}</span>
                  </h2>
                  <span className='srp-group__note'>{kind === 'Diary' ? 'Latest first' : 'Newest first'}</span>
                </div>
                <div className='srp-list'>{group.hits.map(row)}</div>
                {group.more && (
                  <button
                    type='button'
                    className='srp-more'
                    onClick={() => showMore(kind)}
                    disabled={loadingMore === kind}
                  >
                    {loadingMore === kind
                      ? 'Loading…'
                      : `Show more ${plural.toLowerCase()} (${(group.total - group.hits.length).toLocaleString('en-GB')} left)`}
                  </button>
                )}
              </section>
            )
          })
        )}
      </div>
    </div>
  )
}

export default SearchResults
